// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  FulfillmentOrder,
  fulfillmentOrderSchema,
} from '@/fulfillment/api/types';
import { z } from 'zod';

/**
 * Schema for validating the response from the send to 3PL API endpoint.
 */
const sendTo3PLResponseSchema = z.object({
  data: fulfillmentOrderSchema,
  metadata: responseMetadataSchema,
});

/**
 * Sends a fulfillment order to the third-party logistics provider.
 *
 * This operation:
 * 1. Validates the order is in DRAFT status
 * 2. Places the order with the 3PL provider
 * 3. Updates the order status to PENDING
 *
 * @param fulfillmentKey - The key of the fulfillment order to send
 * @returns The updated fulfillment order with status PENDING
 */
export async function sendFulfillmentOrderTo3PL(
  fulfillmentKey: string
): Promise<FulfillmentOrder> {
  const url = `/v1/fulfillment/${fulfillmentKey}/send-to-3pl`;
  const response = await api.post(url, {});

  const { data } = await parseResponse(sendTo3PLResponseSchema, response.data, {
    schemaName: 'SendFulfillmentOrderTo3PL',
    endpoint: url,
  });
  return data;
}
